import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimatedLoader from 'src/components/common/ui/loader/AnimatedLoader';
import CampaignInformation from 'src/components/pages/create-campaign/CampaignInformation';
import CampaignNameForm from 'src/components/pages/create-campaign/CampaignNameForm';
import CategorySelection from 'src/components/pages/create-campaign/CategorySelection';
import FooterNavigation from 'src/components/pages/create-campaign/FooterNavigation';
import GenerateHeadline from 'src/components/pages/create-campaign/GenerateHeadline';
import PaymentForm from 'src/components/pages/create-campaign/PaymentForm';
import ProductServiceForm from 'src/components/pages/create-campaign/ProductServiceForm';
import ProgressSteps from 'src/components/pages/create-campaign/ProgressSteps';
import ReviewLaunch from 'src/components/pages/create-campaign/ReviewLaunch';
import TargetAudienceForm from 'src/components/pages/create-campaign/TargetAudienceForm';
import UploadCreativeForm from 'src/components/pages/create-campaign/UploadCreativeForm';
import { AuthContext } from 'src/contexts';
import { useCampaign } from 'src/contexts/CampaignContext';
import { useHttp, useNotification } from 'src/hooks';

export default function CreateCampaign() {
  const { sendRequest, isLoading, error } = useHttp();
  const { sendRequest: generateContent } = useHttp();
  const { fetchCampaigns } = useCampaign();
  const { accessToken } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [showProductServiceForm, setShowProductServiceForm] = useState(false);
  const [showReviewLaunch, setShowReviewLaunch] = useState(false);
  const [isStepValid, setIsStepValid] = useState(false);
  const notify = useNotification();
  const navigate = useNavigate();

  // Change totalSteps to 5 since we're combining steps 5 and 6
  const totalSteps = 6;

  const [campaignData, setCampaignData] = useState({
    campaignCategory: 'Sales',
    campaignObjective: 'OUTCOME_SALES',
    businessName: '',
    websiteUrl: '',
    campaignName: '',
    accountId: '',
    adAccountId: '',
    facebookPageId: '',
    facebookPageName: '',
    pixelId: '',
    specialAdCategory: '',
    dailyAdBudget: 30,
    keywords: [],
    targetLocations: [],
    targetCountries: [],
    offeredProductsServices: '',
    competitiveAdvantages: '',
    offers: '',
    serviceDescription: '',
    creativeImageHashes: [],
    creativeImageUrls: [],
    creativeVideoIds: [],
    creativeVideoThumbnails: [],
    headlines: [],
    primaryText: "",
  });
  console.log(campaignData, "this is campaign data")
  const generateAdContent = async () => {
    try {
      const response = await generateContent(
        {
          url: `campaign/generate-content`,
          method: 'POST',
          payload: {
            offeredProductsServices: campaignData.offeredProductsServices,
            competitiveAdvantages: campaignData.competitiveAdvantages,
            offers: campaignData.offers,
            serviceDescription: campaignData.serviceDescription,
            businessName: campaignData.businessName
          },
          accessToken,
        },
        data => {
          // Update campaignData with generated content
          setCampaignData(prev => ({
            ...prev,
            headlines: data.headlines || [],
            primaryText: data.primaryText || ""
          }));
        }
      );
      return response;
    } catch (error) {
      console.error('Error generating ad content:', error);
    }
  };
  const handleNext = () => {
    if (currentStep === 3 && !showProductServiceForm) {
      setShowProductServiceForm(true);
    } else if (currentStep === 3 && showProductServiceForm) {
      // Call the API when moving from product info to headlines
      generateAdContent().then(() => {
        setShowProductServiceForm(false);
        setCurrentStep(prev => prev + 1);
      });
    } else if (currentStep === 6) {
      // If we're on step 5 (Generate Headline), show the review screen
      setShowReviewLaunch(true);
    } else {
      setShowProductServiceForm(false);
      setShowReviewLaunch(false);
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (showProductServiceForm) {
      setShowProductServiceForm(false);
    } else if (showReviewLaunch) {
      setShowReviewLaunch(false);
    } else {
      setCurrentStep(prev => Math.max(1, prev - 1));
    }
  };

  const handleSubmit = async () => {
    sendRequest(
      {
        url: `campaign/launch`,
        method: 'POST',
        payload: campaignData,
        accessToken,
      },
      data => {
        notify('Campaign Created successfully!', 'success');
        fetchCampaigns();
        navigate('/dashboard/your-campaign');
        console.log(data);
      },
    );
  };

  useEffect(() => {
    if (error) {
      notify(error || '', 'error');
    }
  }, [error]);

  return (
    <>
      <div
        className={`w-full ${currentStep === 5
          ? 'flex justify-center min-h-screen overflow-y-auto py-10 px-4'
          : 'flex items-center justify-center h-screen w-full'
          }`}
      >
        {isLoading ? (
          <AnimatedLoader />
        ) : (
          <div className="w-full max-w-4xl">
            <>
              <ProgressSteps currentStep={currentStep} />
              {currentStep === 1 && (
                <>
                  <CategorySelection
                    setCampaignData={setCampaignData}
                    campaignData={campaignData}
                    setIsStepValid={setIsStepValid}
                  />
                  <CampaignInformation
                    setCampaignData={setCampaignData}
                    campaignData={campaignData}
                    setIsStepValid={setIsStepValid}
                  />
                </>
              )}
              {currentStep === 2 && (
                <CampaignNameForm
                  setCampaignData={setCampaignData}
                  campaignData={campaignData}
                  setIsStepValid={setIsStepValid}
                />
              )}
              {currentStep === 3 && !showProductServiceForm && (
                <TargetAudienceForm
                  setCampaignData={setCampaignData}
                  campaignData={campaignData}
                  setIsStepValid={setIsStepValid}
                />
              )}
              {currentStep === 3 && showProductServiceForm && (
                <ProductServiceForm
                  setCampaignData={setCampaignData}
                  campaignData={campaignData}
                  setIsStepValid={setIsStepValid}
                />
              )}
              {currentStep === 4 && (
                <UploadCreativeForm
                  setCampaignData={setCampaignData}
                  campaignData={campaignData}
                  setIsStepValid={setIsStepValid}
                />
              )}
              {currentStep === 5 && (
                <PaymentForm
                  setCampaignData={setCampaignData}
                  campaignData={campaignData}
                  setIsStepValid={setIsStepValid}
                />
              )}
              {currentStep === 6 && !showReviewLaunch && (
                <GenerateHeadline
                  setCampaignData={setCampaignData}
                  campaignData={campaignData}
                  setIsStepValid={setIsStepValid}
                />
              )}
              {currentStep === 6 && showReviewLaunch && (
                <ReviewLaunch
                  data={{
                    category: campaignData.campaignCategory || 'N/A',
                    objective: campaignData.campaignObjective || 'N/A',
                    businessName: campaignData.businessName || 'N/A',
                    websiteUrl: campaignData.websiteUrl || 'N/A',
                    campaignName: campaignData.campaignName || 'N/A',
                    facebookPage: campaignData.facebookPageName || 'N/A',
                    dailyBudget: campaignData.dailyAdBudget
                      ? `$${campaignData.dailyAdBudget}`
                      : 'N/A',
                    keywords: campaignData.keywords.length > 0 ? campaignData.keywords : ['N/A'],
                    locations:
                      campaignData.targetCountries.length > 0
                        ? campaignData.targetCountries.map(loc => loc.label || loc).join(', ')
                        : 'N/A',
                    offeredProductsServices: campaignData.offeredProductsServices || 'N/A',
                    competitiveAdvantages: campaignData.competitiveAdvantages || 'N/A',
                    offers: campaignData.offers || 'N/A',
                    serviceDescription: campaignData.serviceDescription || 'N/A',
                    creativeImages:
                      campaignData.creativeImageUrls.length > 0
                        ? campaignData.creativeImageUrls
                        : [],
                    creativeVideos:
                      campaignData.creativeVideoIds.length > 0 ? campaignData.creativeVideoIds : [],
                    creativeVideoThumbnails: campaignData.creativeVideoThumbnails,
                    // headlines: campaignData.headlines || ['N/A'],
                  }}
                />
              )}
              <FooterNavigation
                currentStep={currentStep}
                totalSteps={totalSteps}
                handleNext={handleNext}
                handleBack={handleBack}
                handleSubmit={handleSubmit}
                type="campaign"
                isStepValid={isStepValid}
                showReviewLaunch={showReviewLaunch} // Add this line

              />
            </>
          </div>
        )}
      </div>
    </>
  );
}